import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Coins, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 15; // ~30 seconds

export default function CreditsSuccess() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const account = getCurrentAccount();
  const sessionId = sp.get('session_id');
  const [status, setStatus] = useState('checking');   // checking | paid | pending | expired | error
  const [payload, setPayload] = useState(null);
  const [attempts, setAttempts] = useState(0);
  // Pick up the resume marker set by WalletTopupCard so we can offer a
  // one-click "Return to Astrid" that reopens the exact chat + primes the
  // continuation prompt. TTL-guarded: >30 min old markers are ignored.
  const [resumeMarker, setResumeMarker] = useState(null);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('ai_agent_resume');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.conv_id && Number(parsed.expires_at || 0) > Date.now()) {
        setResumeMarker(parsed);
      }
    } catch { /* ignore */ }
  }, []);
  const returnToAstrid = () => {
    if (!resumeMarker) return;
    try { window.localStorage.removeItem('ai_agent_resume'); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('ai-agent:open', {
      detail: {
        tab: 'chats',
        conv_id: resumeMarker.conv_id,
        prompt: resumeMarker.prompt || 'I just topped up — please continue where we left off.',
      },
    }));
    // Also route the browser home so the drawer is the focal point.
    navigate('/dashboard');
  };

  const poll = useCallback(async () => {
    if (!sessionId || !account?.id) return;
    try {
      const { data } = await api.get(`/credits/checkout/status/${sessionId}`, {
        params: { account_id: account.id },
      });
      setPayload(data);
      if (data.payment_status === 'paid') {
        setStatus('paid');
        if (data.credits_granted_now) {
          toast.success(`Added ${data.credits} credits to your wallet`);
        }
        return true;
      }
      if (data.status === 'expired') {
        setStatus('expired');
        return true;
      }
      setStatus('pending');
      return false;
    } catch (e) {
      setStatus('error');
      return true;
    }
  }, [sessionId, account?.id]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < MAX_POLLS && !cancelled; i += 1) {
        setAttempts(i + 1);
        const done = await poll();
        if (done) return;
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, poll]);

  if (!sessionId) {
    return (
      <MainLayout>
        <div className="max-w-lg mx-auto text-center py-20">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
          <h1 className="text-lg font-bold mt-3">Missing session</h1>
          <p className="text-sm text-slate-500">This page was opened without a checkout session.</p>
          <Button className="mt-4 rounded-none" onClick={() => navigate('/dashboard')}>Go home</Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-xl mx-auto py-14 px-4" data-testid="credits-success-page">
        {status === 'checking' || status === 'pending' ? (
          <div className="text-center">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-emerald-600" />
            <h1 className="text-xl font-bold mt-4">Confirming your payment…</h1>
            <p className="text-sm text-slate-500 mt-1">
              Attempt {attempts}/{MAX_POLLS}. This usually takes a few seconds.
            </p>
            {resumeMarker && attempts >= 3 && (
              <div className="mt-6" data-testid="credits-success-resume-pending-wrap">
                <p className="text-xs text-slate-400 mb-2">
                  Payment confirmation is taking longer than usual. You can return to Astrid &mdash; she&apos;ll see the balance as soon as it lands.
                </p>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={returnToAstrid}
                  data-testid="credits-success-resume-astrid-pending"
                >
                  Return to Astrid <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        ) : status === 'paid' ? (
          <div className="text-center" data-testid="credits-success-paid">
            <div className="mx-auto h-16 w-16 rounded-none border-2 border-emerald-500 bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold mt-4">Payment successful</h1>
            <p className="text-sm text-slate-600 mt-2">
              <span className="inline-flex items-center gap-1 font-semibold text-slate-800">
                <Coins className="h-4 w-4 text-amber-500" /> +{payload?.credits} credits
              </span>{' '}
              have been added to your wallet.
            </p>
            <div className="mt-2 text-xs text-slate-500">
              Session <code className="font-mono">{sessionId.slice(-12)}</code> · ${(payload?.amount_total / 100).toFixed(2)} {payload?.currency?.toUpperCase()}
            </div>
            <div className="mt-6 flex justify-center gap-2 flex-wrap">
              {resumeMarker && (
                <Button
                  className="rounded-none bg-emerald-600 hover:bg-emerald-700"
                  onClick={returnToAstrid}
                  data-testid="credits-success-resume-astrid"
                >
                  Return to Astrid <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              {!resumeMarker && (
                <Button className="rounded-none bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/ubm/migration')} data-testid="credits-success-migration-btn">
                  Start a migration <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              <Button variant="outline" className="rounded-none" onClick={() => navigate('/settings/credits')} data-testid="credits-success-usage-btn">
                View usage
              </Button>
            </div>
          </div>
        ) : status === 'expired' ? (
          <div className="text-center" data-testid="credits-success-expired">
            <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
            <h1 className="text-xl font-bold mt-3">Session expired</h1>
            <p className="text-sm text-slate-500 mt-1">Your Stripe session expired before payment completed.</p>
            <Button className="mt-4 rounded-none" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>
          </div>
        ) : (
          <div className="text-center" data-testid="credits-success-error">
            <AlertTriangle className="h-10 w-10 mx-auto text-rose-500" />
            <h1 className="text-xl font-bold mt-3">Couldn&apos;t confirm status</h1>
            <p className="text-sm text-slate-500 mt-1">
              Your payment may still succeed — refresh this page or check the wallet chip in a minute.
            </p>
            <Button className="mt-4 rounded-none" onClick={poll}>Retry</Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
